import React, { useContext, useEffect, useMemo, useState } from "react";
import { Context } from "../../../../Context";
import operationData from "../../../../../Operation/data.json";
import { fetchOrders } from "../../../../utils/foodDeliveryApi";
import FoodDeliveryTabs from "./FoodDeliveryTabs";
import { FiSearch, FiFilter, FiRefreshCw, FiChevronDown, FiChevronUp } from "react-icons/fi";

const STATUS_LIST = [
  'all',
  'order_placed',
  'accepted',
  'preparing',
  'ready',
  'out_for_delivery',
  'delivered',
  'rejected',
  'cancelled'
];

const STATUS_CONFIG = {
  cancelled: { bg: "bg-red-100", text: "text-red-800", border: "border-red-300" },
  rejected: { bg: "bg-red-100", text: "text-red-800", border: "border-red-300" },
  returned: { bg: "bg-red-100", text: "text-red-800", border: "border-red-300" },
  delivered: { bg: "bg-green-100", text: "text-green-800", border: "border-green-300" },
  order_placed: { bg: "bg-blue-100", text: "text-blue-800", border: "border-blue-300" },
  placed: { bg: "bg-blue-100", text: "text-blue-800", border: "border-blue-300" },
  pending: { bg: "bg-blue-100", text: "text-blue-800", border: "border-blue-300" },
  accepted: { bg: "bg-blue-100", text: "text-blue-800", border: "border-blue-300" },
  preparing: { bg: "bg-blue-100", text: "text-blue-800", border: "border-blue-300" },
  ready: { bg: "bg-blue-100", text: "text-blue-800", border: "border-blue-300" },
  out_for_delivery: { bg: "bg-blue-100", text: "text-blue-800", border: "border-blue-300" },
};

const normalizeListResponse = (response) => {
  if (Array.isArray(response)) return response;
  const candidate = response?.data || response?.items || response?.result || response?.body;
  return Array.isArray(candidate) ? candidate : [];
};

const getOrderStatus = (order) => (order.orderStatus || order.status || "").toString().toLowerCase();

const OrderHistory = () => {
  const { institutionData } = useContext(Context);
  const institution = operationData.foodDeliveryInstitution;
  const activeInstitution = institution || institutionData?.institution || "";

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMode, setFilterMode] = useState('all'); // 'day', 'month', 'year', 'all'
  const [viewDate, setViewDate] = useState(new Date());
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showInsights, setShowInsights] = useState(false);

  const loadOrders = async () => {
    if (!activeInstitution) return;
    setLoading(true);
    try {
      const res = await fetchOrders(activeInstitution);
      const sorted = normalizeListResponse(res).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setOrders(sorted);
    } catch (err) {
      console.error('Error loading orders:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, [activeInstitution]);

  const filteredOrders = useMemo(() => {
    let result = orders;

    // 1. Text Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(o =>
        (o.orderId || '').toString().toLowerCase().includes(q) ||
        (o.customerName || '').toLowerCase().includes(q) ||
        (o.customerPhone || o.phone || '').includes(q)
      );
    }

    // 2. Date Filtering
    const vY = viewDate.getFullYear();
    const vM = viewDate.getMonth();
    const vD = viewDate.getDate();

    result = result.filter(o => {
      const d = new Date(o.createdAt);
      if (filterMode === 'day') {
        return d.getFullYear() === vY && d.getMonth() === vM && d.getDate() === vD;
      } else if (filterMode === 'month') {
        return d.getFullYear() === vY && d.getMonth() === vM;
      } else if (filterMode === 'year') {
        return d.getFullYear() === vY;
      } else if (filterMode === 'all') {
        return true;
      }
      return true;
    });

    // 3. Status Filtering
    if (statusFilter !== 'all') {
      result = result.filter(o => getOrderStatus(o) === statusFilter);
    }

    return result;
  }, [orders, searchQuery, filterMode, viewDate, statusFilter]);

  // Calculate Insights
  const insights = useMemo(() => {
    const itemMap = {};
    const custMap = {};

    filteredOrders.forEach(o => {
      // Items
      (o.items || o.cart || []).forEach(i => {
        const name = i.itemName || i.name;
        if (name) itemMap[name] = (itemMap[name] || 0) + (i.quantity || 1);
      });
      // Customers
      const cName = o.customerName;
      if (cName && cName.toLowerCase() !== 'guest') {
        custMap[cName] = (custMap[cName] || 0) + 1;
      }
    });

    const topItems = Object.entries(itemMap).sort((a, b) => b[1] - a[1]).slice(0, 5);
    const topCusts = Object.entries(custMap).sort((a, b) => b[1] - a[1]).slice(0, 5);

    return { topItems, topCusts };
  }, [filteredOrders]);

  // Calculate Metrics
  const metrics = useMemo(() => {
    const totalOrders = filteredOrders.length;
    const ratedOrders = filteredOrders.filter(o => o.rating && o.rating > 0);
    const avgRating = ratedOrders.length > 0
      ? (ratedOrders.reduce((sum, o) => sum + Number(o.rating), 0) / ratedOrders.length).toFixed(1)
      : 0;

    return {
      totalOrders,
      averageRating: avgRating,
      ratingCount: ratedOrders.length
    };
  }, [filteredOrders]);

  const getFilterLabel = (mode, date) => {
    if (mode === 'day') return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    if (mode === 'month') return date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    if (mode === 'year') return date.getFullYear().toString();
    if (mode === 'all') return 'All Time';
    return '';
  };

  const navigateDate = (direction) => {
    const newDate = new Date(viewDate);
    if (filterMode === 'day') {
      newDate.setDate(newDate.getDate() + direction);
    } else if (filterMode === 'month') {
      newDate.setMonth(newDate.getMonth() + direction);
    } else if (filterMode === 'year') {
      newDate.setFullYear(newDate.getFullYear() + direction);
    }
    setViewDate(newDate);
  };

  const getStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    return STATUS_CONFIG[s] || { bg: "bg-gray-100", text: "text-gray-800", border: "border-gray-300" };
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-gray-50">
        <FoodDeliveryTabs />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mb-4"></div>
            <p>Loading order history...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gray-50">
      <FoodDeliveryTabs />
      <div className="px-4 py-6 lg:px-8">
        <div className="mx-auto max-w-5xl space-y-4">
          {/* Header */}
          <div className="bg-white px-6 py-6 rounded-lg shadow-sm border border-gray-200 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-extrabold text-[#1F2937]">Food Delivery — Order History</h1>
              <p className="mt-1 text-sm text-gray-500">{activeInstitution}</p>
            </div>
            <button
              onClick={loadOrders}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <FiRefreshCw size={18} />
              Refresh
            </button>
          </div>

          {/* Controls Section */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            {/* Search & Filter Row */}
            <div className="flex gap-4 mb-4">
              <div className="flex-1 flex items-center gap-2 bg-gray-50 border border-gray-300 rounded-lg px-3 py-2">
                <FiSearch size={16} className="text-gray-500" />
                <input
                  type="text"
                  placeholder="Search orders..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 bg-transparent outline-none text-sm"
                />
              </div>
              <button
                onClick={() => setShowFilterModal(!showFilterModal)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition ${
                  filterMode !== 'all' ? 'bg-blue-50 border-blue-300' : 'border-gray-300 hover:bg-gray-50'
                }`}
              >
                <FiFilter size={18} className={filterMode !== 'all' ? 'text-blue-600' : 'text-gray-600'} />
                <span className="text-sm font-medium">{getFilterLabel(filterMode, viewDate)}</span>
                <FiChevronDown size={16} />
              </button>
            </div>

            {/* Metrics & Status Filter Row */}
            <div className="flex justify-between items-center">
              <div className="flex gap-4">
                <div className="bg-gray-50 rounded-lg px-3 py-2 text-sm">
                  <span className="text-gray-600">Orders: </span>
                  <span className="font-bold text-gray-900">{metrics.totalOrders}</span>
                </div>
                <div className="bg-gray-50 rounded-lg px-3 py-2 text-sm">
                  <span className="text-gray-600">Avg Rating: </span>
                  <span className="font-bold text-gray-900">{metrics.averageRating} ⭐</span>
                  <span className="text-gray-500 text-xs"> ({metrics.ratingCount})</span>
                </div>
              </div>
              <div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {STATUS_LIST.map(status => (
                    <option key={status} value={status}>
                      {status === 'all' ? 'All Statuses' : status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Filter Modal */}
            {showFilterModal && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Time Period</label>
                  <div className="flex gap-2">
                    {['day', 'month', 'year', 'all'].map(m => (
                      <button
                        key={m}
                        onClick={() => setFilterMode(m)}
                        className={`px-3 py-1 rounded text-sm font-medium transition ${
                          filterMode === m
                            ? 'bg-gray-900 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {m.charAt(0).toUpperCase() + m.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {filterMode !== 'all' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select Date</label>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => navigateDate(-1)}
                        className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
                      >
                        ←
                      </button>
                      <input
                        type="date"
                        value={viewDate.toISOString().split('T')[0]}
                        onChange={(e) => setViewDate(new Date(e.target.value))}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        onClick={() => navigateDate(1)}
                        className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
                      >
                        →
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Insights Toggle */}
          <button
            onClick={() => setShowInsights(!showInsights)}
            className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:text-blue-700 font-medium"
          >
            📊 {showInsights ? 'Hide' : 'Show'} Popular Items & Customers
            {showInsights ? <FiChevronUp /> : <FiChevronDown />}
          </button>

          {/* Insights Section */}
          {showInsights && (insights.topItems.length > 0 || insights.topCusts.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Popular Items */}
              {insights.topItems.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <h3 className="font-bold text-gray-900 mb-4">Popular Items</h3>
                  <div className="space-y-2">
                    {insights.topItems.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{
                            backgroundColor: ['#3B82F6', '#111827', '#10B981', '#EF4444', '#8B5CF6'][idx % 5]
                          }}></div>
                          <span className="text-sm text-gray-700">{item[0]}</span>
                        </div>
                        <span className="font-bold text-gray-900">{item[1]}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Customers */}
              {insights.topCusts.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <h3 className="font-bold text-gray-900 mb-4">Top Customers</h3>
                  <div className="space-y-2">
                    {insights.topCusts.map((cust, idx) => (
                      <div key={idx} className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{
                            backgroundColor: ['#3B82F6', '#111827', '#10B981', '#EF4444', '#8B5CF6'][(idx + 2) % 5]
                          }}></div>
                          <span className="text-sm text-gray-700">{cust[0]}</span>
                        </div>
                        <span className="font-bold text-gray-900">{cust[1]} orders</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Orders List */}
          <div className="space-y-4">
            {filteredOrders.length === 0 ? (
              <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                <p className="text-gray-500 text-lg">No orders found</p>
              </div>
            ) : (
              filteredOrders.map((order, index) => {
                const orderId = order.orderId || order.id || index;
                const status = getOrderStatus(order);
                const statusColor = getStatusColor(status);
                const items = Array.isArray(order.items) ? order.items : (order.cart || []);
                return (
                  <div key={orderId} className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition">
                    {/* Card Header */}
                    <div className="flex justify-between items-start p-4 border-b border-gray-100">
                      <div>
                        <h3 className="font-bold text-lg text-gray-900">#{String(orderId).slice(-6).toUpperCase()}</h3>
                        <p className="text-gray-500 text-sm">
                          {new Date(order.createdAt).toLocaleDateString()} • {new Date(order.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium border ${statusColor.bg} ${statusColor.text} ${statusColor.border}`}>
                        {status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                      </span>
                    </div>

                    {/* Card Body */}
                    <div className="p-4 space-y-3">
                      {/* Customer Info */}
                      <div className="flex gap-6">
                        <div>
                          <p className="text-gray-600 text-sm">👤 Customer</p>
                          <p className="font-medium text-gray-900">{order.customerName || 'Guest'}</p>
                        </div>
                        <div>
                          <p className="text-gray-600 text-sm">📞 Phone</p>
                          <p className="font-medium text-gray-900">{order.customerPhone || order.phone || 'N/A'}</p>
                        </div>
                      </div>

                      {/* Items */}
                      {items.length > 0 && (
                        <div>
                          <p className="text-gray-600 text-sm mb-1">Items:</p>
                          <div className="flex flex-wrap gap-2">
                            {items.slice(0, 2).map((item, idx) => (
                              <span key={idx} className="bg-gray-100 px-2 py-1 rounded text-sm text-gray-700">
                                {item.quantity}x {item.itemName || item.name}
                              </span>
                            ))}
                            {items.length > 2 && (
                              <span className="bg-gray-100 px-2 py-1 rounded text-sm text-gray-600">
                                +{items.length - 2} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Review */}
                      {(order.ratingDescription || order.review || order.feedback) && (
                        <div className="bg-blue-50 border-l-4 border-blue-500 rounded p-3">
                          <p className="text-blue-900 text-sm italic">
                            💬 {order.ratingDescription || order.review || order.feedback}
                          </p>
                        </div>
                      )}

                      {/* Footer */}
                      <div className="flex justify-between items-end pt-2 border-t border-gray-100">
                        <div>
                          <p className="text-gray-600 text-sm">TOTAL</p>
                          <p className="text-2xl font-bold text-gray-900">₹{Number(order.totalAmount || order.amount || 0).toFixed(2)}</p>
                        </div>
                        {order.rating && order.rating > 0 && (
                          <div className="bg-amber-400 text-white px-3 py-1 rounded-full font-bold text-sm">
                            ⭐ {order.rating}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderHistory;
